import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  styled,
  Autocomplete,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import AddIcon from '@mui/icons-material/Add';
import { TripFormData, TripInformationSectionProps, PlacePrediction, DestinationWithDistance } from '../../types/tripInformationNewTrip';
import { 
  HeaderBox,
  ContentBox,
  DestinationChipsContainer,
  ChipNumberCircle,
  DestinationAutocomplete,
  AddButton,
  PricingSection,
  AgreedPriceInput,
  FooterBox, 
  DestinationChipStyles
} from '../layout/styles/TripInformationNewTrips.styles';
import { getPlacePredictions, initGooglePlacesApi, resetSessionToken } from '../../utils/googlePlacesApi';
import { calculateDistance } from '../../utils/distanceCalculator';
import ServiceSelectionForm from './ServiceSelectionForm';
import TripPricingCalculator from './TripPricingCalculator';

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: 0,
  backgroundColor: '#1e1e1e',
  color: '#ffffff',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  border: '1px solid #333'
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2)
}));

const FormField = styled(Box)(({ theme }) => ({
  flex: '1 1 calc(50% - 16px)',
  minWidth: '240px'
}));

const TripInformationSection: React.FC<TripInformationSectionProps> = ({
  tripData,
  onTripDataChange,
  errors,
  onErrorsChange,
  serviceOptions,
  fleetOptions,
  pricingTable,
  isSubmitting,
  onSubmit,
  onClose,
  isMumbaiAirportService,
  isOnewayService,
  isPerPaxService,
  isOtherService,
  airportPickupDrop,
  onewayRoute,
  perPaxRoute,
  onMumbaiAirportServiceChange,
  onOnewayServiceChange,
  onPerPaxServiceChange,
  onOtherServiceChange,
  onAirportPickupDropChange,
  onOnewayRouteChange,
  onPerPaxRouteChange,
  onCalculatedPriceChange,
  onCalculatedDaysChange,
  onEstimatedPriceChange
}) => {
  const [calculatedDays, setCalculatedDays] = useState(1);
  const [customKm, setCustomKm] = useState(0);
  const [showPricingCalculator, setShowPricingCalculator] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);
  const [distancePriceCalculation, setDistancePriceCalculation] = useState<number | null>(null);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  
  // Add state to track if user has manually interacted with agreed price
  const [hasUserEditedPrice, setHasUserEditedPrice] = useState(false);

  // Add state for outstation service
  const [outstationSourceCity, setOutstationSourceCity] = useState('');

  // Multiple destinations state
  const [destinationsList, setDestinationsList] = useState<string[]>([]);
  const [destinationsWithDistance, setDestinationsWithDistance] = useState<DestinationWithDistance[]>([]);
  const [newDestination, setNewDestination] = useState('');
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([]);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [totalDistance, setTotalDistance] = useState<number | null>(null);
  
  // State for drag and drop
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  
   // Handle estimated price change from pricing calculator
  const handleEstimatedPriceChange = (price: number) => {
    setEstimatedPrice(price);
    
    // Notify parent component
    if (onEstimatedPriceChange) {
      onEstimatedPriceChange(price);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.stopPropagation(); // Stop event from bubbling up
    setDraggedItemIndex(index);
    
    // Set the drag image to be the current element
    if (e.dataTransfer.setDragImage) {
      e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
    }
    
    // Required for Firefox and other browsers
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    
    // Add some styling to the dragged element
    e.currentTarget.style.opacity = '0.4';
    e.currentTarget.style.cursor = 'grabbing';
  };
  
  // Handle drag over
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Necessary to allow dropping
    e.stopPropagation(); // Stop event from bubbling up
    e.dataTransfer.dropEffect = 'move';
  };
  
  // Handle drag end
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Reset styles
    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.cursor = 'grab';
    e.currentTarget.style.backgroundColor = '';
    
    setDraggedItemIndex(null);
  };
  
  // Handle drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Reset background style
    e.currentTarget.style.backgroundColor = '';
    
    if (draggedItemIndex === null || draggedItemIndex === dropIndex) {
      return;
    }
    
    // Reorder the destinations list
    const reorderedDestinations = [...destinationsList];
    const [movedDestination] = reorderedDestinations.splice(draggedItemIndex, 1);
    reorderedDestinations.splice(dropIndex, 0, movedDestination);
    
    // Update state
    setDestinationsList(reorderedDestinations);
    
    // Update trip data
    onTripDataChange({
      ...tripData,
      destinationCities: reorderedDestinations.join(' | '),
      destinationCitiesList: reorderedDestinations
    });
    
    // Recalculate distances with the new order
    calculateDistances();
    
    // Reset drag state
    setDraggedItemIndex(null);
  };
  
  // Handle drag enter
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only highlight if we're actually dragging something
    if (draggedItemIndex !== null) {
      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
    }
  };
  
  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.backgroundColor = '';
  };

  const shouldHideEndDate = useMemo(() => {
    if (!tripData.travelServiceDetailId || !serviceOptions.length) return false;
    
    const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
    const serviceName = selectedService?.name?.toLowerCase() || '';
    
    return isMumbaiAirportService || 
           isOnewayService || 
           isPerPaxService ||
           isOtherService ||
           serviceName.includes('pune local') ||
           serviceName.includes('airport') ||
           serviceName.includes('oneway') ||
           serviceName.includes('one way') ||
           serviceName.includes('others') ||
           serviceName.includes('per pax');
  }, [tripData.travelServiceDetailId, serviceOptions, isMumbaiAirportService, isOnewayService, isPerPaxService, isOtherService]);

  // Add computed property to check if Pune Local service is selected
  const isPuneLocalService = useMemo(() => {
    if (!tripData.travelServiceDetailId || !serviceOptions.length) return false;
    
    const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
    const serviceName = selectedService?.name?.toLowerCase() || '';
    
    return serviceName.includes('pune local');
  }, [tripData.travelServiceDetailId, serviceOptions]);

  // Add computed property to check if Outstation service is selected
  const isOutstationService = useMemo(() => {
    if (!tripData.travelServiceDetailId || !serviceOptions.length) return false;
    
    const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
    const serviceName = selectedService?.name?.toLowerCase() || '';
    
    return serviceName.includes('outstation');
  }, [tripData.travelServiceDetailId, serviceOptions]);

  // Add computed property to check if any service is selected
  const isServiceSelected = useMemo(() => {
    return !!(tripData.travelServiceDetailId && tripData.travelServiceDetailId !== '');
  }, [tripData.travelServiceDetailId]);

  // Add useEffect to ensure Pune Local service always has correct source city
  useEffect(() => {
    if (isPuneLocalService && tripData.sourceCity !== 'Pune') {
      onTripDataChange({
        ...tripData,
        sourceCity: 'Pune'
      });
      
      // Clear source city validation error when automatically setting Pune
      if (errors.sourceCity) {
        const newErrors = { ...errors };
        delete newErrors.sourceCity;
        onErrorsChange(newErrors);
      }
    }
  }, [isPuneLocalService]); // Remove tripData.sourceCity from dependencies to avoid infinite loop

  useEffect(() => {
    if (isMumbaiAirportService || isOnewayService || isPerPaxService || isPuneLocalService) {
      setCalculatedDays(1);
      if (onCalculatedDaysChange) {
        onCalculatedDaysChange(1);
      }
      return;
    }
    if (isOutstationService && tripData.startDatetime && tripData.endDatetime) {
      const start = new Date(tripData.startDatetime);
      const end = new Date(tripData.endDatetime);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const days = Math.max(1, diffDays);
      setCalculatedDays(days);
      if(onCalculatedDaysChange) {
        onCalculatedDaysChange(days);
      }
    } else {
      setCalculatedDays(1);
    }
  }, [tripData.startDatetime, tripData.endDatetime, onCalculatedDaysChange]);
  

  useEffect(() => {
    const shouldShowCalculator = tripData.travelServiceDetailId && 
                                tripData.travelfleetDetailId && 
                                tripData.sourceCity && 
                                tripData.destinationCities &&
                                tripData.startDatetime;
    setShowPricingCalculator(!!shouldShowCalculator);
  }, [tripData.travelServiceDetailId, tripData.travelfleetDetailId, tripData.sourceCity, tripData.destinationCities, tripData.startDatetime]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special handling for destinationCities to keep it in sync with destinationsList
    if (name === 'destinationCities') {
      // Only update if the user is manually editing the hidden field
      // This should rarely happen since we're using the multi-destination UI
      const destinations = value.split(' | ').map(d => d.trim()).filter(d => d);
      setDestinationsList(destinations);
      onTripDataChange({ 
        ...tripData, 
        destinationCities: value,
        destinationCitiesList: destinations
      });
    } else {
      // Normal field handling
      onTripDataChange({ ...tripData, [name]: value });
    }

    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      onErrorsChange(newErrors);
    }
  };  const handleDateChange = (name: string, date: Date | null) => {
    if (date) {
      // Create a new Date object to ensure we have the exact local time selected
      const localDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
      );     
      onTripDataChange({ ...tripData, [name]: localDate });
    } else {
      onTripDataChange({ ...tripData, [name]: null });
    }
    
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      onErrorsChange(newErrors);
    }
  };

  const handleServiceChange = (serviceId: string) => {
    const selectedService = serviceOptions.find(s => s.id === Number(serviceId));
    const serviceName = selectedService?.name?.toLowerCase() || '';
    const isMumbaiAirport = serviceName.includes('mumbai airport') || serviceName.includes('airport');
    const isOneway = serviceName.includes('oneway') || serviceName.includes('one way');
    const isPerPax = serviceName.includes('per pax');
    const isOtherService = serviceName.includes('others');
    const isPuneLocal = serviceName.includes('pune local');
    const isOutstation = serviceName.includes('outstation');

    onMumbaiAirportServiceChange(isMumbaiAirport);
    onOnewayServiceChange(isOneway);
    onPerPaxServiceChange(isPerPax);
    onOtherServiceChange(isOtherService);
    
    if (!isMumbaiAirport) {
      onAirportPickupDropChange('');
    }
    if (!isOneway) {
      onOnewayRouteChange('');
    }
    if (!isPerPax) {
      onPerPaxRouteChange('');
    }
  
    // Always reset outstation source city state when service changes
    setOutstationSourceCity('');
    
    // Always clear destinations list when service changes
    setDestinationsList([]);
    
    // Handle Pune Local service - automatically set destination to "N/A" and source city to "Pune"
    if (isPuneLocal) {
      setDestinationsList(['N/A']);
      onTripDataChange({ 
        ...tripData, 
        travelServiceDetailId: serviceId,
        travelfleetDetailId: '',
        endDatetime: null, // Pune Local doesn't need end date
        sourceCity: 'Pune', // Set source city to Pune for Pune Local service
        destinationCities: 'N/A',
        destinationCitiesList: ['N/A']
      });
    } else {
      onTripDataChange({ 
        ...tripData, 
        travelServiceDetailId: serviceId,
        travelfleetDetailId: '',
        endDatetime: (isMumbaiAirport || isOneway || isPerPax || serviceName.includes('pune local')) ? null : tripData.endDatetime,
        sourceCity: '', // Always reset source city
        destinationCities: '', // Always reset destination cities
        destinationCitiesList: [] // Always reset destination cities list
      });
    }

    if (errors.service) {
      const newErrors = { ...errors };
      delete newErrors.service;
      onErrorsChange(newErrors);
    }
  };

  // Add handler for outstation source city selection
  const handleOutstationSourceCityChange = (selectedCity: string) => {
    setOutstationSourceCity(selectedCity);
    
    onTripDataChange({
      ...tripData,
      sourceCity: selectedCity
    });
    
    // Clear source city related errors
    const newErrors = { ...errors };
    delete newErrors.sourceCity;
    onErrorsChange(newErrors);
  };

  const handleFleetChange = (fleetId: string) => {
    onTripDataChange({ 
      ...tripData, 
      travelfleetDetailId: fleetId
    });

    if (errors.serviceFleet || errors.fleet) {
      const newErrors = { ...errors };
      delete newErrors.serviceFleet;
      delete newErrors.fleet;
      onErrorsChange(newErrors);
    }
  };

  const handleAirportSelectionChange = (selection: string) => {
    onAirportPickupDropChange(selection);
    
    if (selection === 'pickup') {
      // Set single destination and update destinations list
      const newDestination = 'Pune';
      setDestinationsList([newDestination]);
      
      onTripDataChange({
        ...tripData,
        sourceCity: 'Mumbai Airport',
        destinationCities: newDestination,
        destinationCitiesList: [newDestination]
      });
    } else if (selection === 'drop') {
      // Set single destination and update destinations list
      const newDestination = 'Mumbai Airport';
      setDestinationsList([newDestination]);
      
      onTripDataChange({
        ...tripData,
        sourceCity: 'Pune',
        destinationCities: newDestination,
        destinationCitiesList: [newDestination]
      });
    }
    
    const newErrors = { ...errors };
    delete newErrors.sourceCity;
    delete newErrors.destinationCities;
    delete newErrors.airportPickupDrop;
    onErrorsChange(newErrors);
  };

  const handleOnewaySelectionChange = (selection: string) => {
    onOnewayRouteChange(selection);
    
    if (selection === 'pune-to-mumbai') {
      // Set single destination and update destinations list
      const newDestination = 'Mumbai';
      setDestinationsList([newDestination]);
      
      onTripDataChange({
        ...tripData,
        sourceCity: 'Pune',
        destinationCities: newDestination,
        destinationCitiesList: [newDestination]
      });
    } else if (selection === 'mumbai-to-pune') {
      // Set single destination and update destinations list
      const newDestination = 'Pune';
      setDestinationsList([newDestination]);
      
      onTripDataChange({
        ...tripData,
        sourceCity: 'Mumbai',
        destinationCities: newDestination,
        destinationCitiesList: [newDestination]
      });
    }
    
    const newErrors = { ...errors };
    delete newErrors.sourceCity;
    delete newErrors.destinationCities;
    delete newErrors.onewayRoute;
    onErrorsChange(newErrors);
  };

  const handlePerPaxSelectionChange = (selection: string) => {
    onPerPaxRouteChange(selection);
    
    if (selection === 'pune-to-mumbai') {
      // Set single destination and update destinations list
      const newDestination = 'Mumbai';
      setDestinationsList([newDestination]);
      
      onTripDataChange({
        ...tripData,
        sourceCity: 'Pune',
        destinationCities: newDestination,
        destinationCitiesList: [newDestination]
      });
    } else if (selection === 'mumbai-to-pune') {
      // Set single destination and update destinations list
      const newDestination = 'Pune';
      setDestinationsList([newDestination]);
      
      onTripDataChange({
        ...tripData,
        sourceCity: 'Mumbai',
        destinationCities: newDestination,
        destinationCitiesList: [newDestination]
      });
    }
    
    const newErrors = { ...errors };
    delete newErrors.sourceCity;
    delete newErrors.destinationCities;
    delete newErrors.perPaxRoute;
    onErrorsChange(newErrors);
  };

  const getSelectedFleetDetails = () => {
    if (!tripData.travelServiceDetailId || !tripData.travelfleetDetailId) return null;
    
    const serviceIndex = Number(tripData.travelServiceDetailId) - 1;
    const fleetIndex = Number(tripData.travelfleetDetailId) - 1;
    
    if (serviceIndex < 0 || fleetIndex < 0 || !pricingTable?.data) return null;
    
    const service = pricingTable.data[serviceIndex];
    if (!service?.carOptions) return null;
    
    return service.carOptions[fleetIndex] || null;
  };

  const getServiceType = () => {
    if (!tripData.travelServiceDetailId || !serviceOptions.length) return null;
    
    const selectedService = serviceOptions.find(s => s.id === Number(tripData.travelServiceDetailId));
    const serviceName = selectedService?.name?.toLowerCase() || '';
    
    if (serviceName.includes('pune local')) return 'LOCAL';
    if (serviceName.includes('outstation')) return 'OUTSTATION';
    if (serviceName.includes('mumbai airport') || serviceName.includes('airport')) return 'AIRPORT';
    if (serviceName.includes('oneway') || serviceName.includes('one way')) return 'ONEWAY';
    if (serviceName.includes('per pax')) return 'PERPAX';
    
    return 'OTHER';
  };

  const selectedFleetInfo = getSelectedFleetDetails();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
     // Debug log
    onSubmit();
  };

  const handleDiscountedPriceChange = (price: number) => {
    setDiscountedPrice(price);
  };

  const handleDistancePriceCalculationChange = (price: number | null) => {
    setDistancePriceCalculation(price);
  };

  // Compute the default agreed price based on the priority:
  // 1. If distancePriceCalculation is available, use it
  // 2. If not, use discountedPrice
  // 3. Return null if no calculated price available
  const getCalculatedPrice = () => {
    // Priority 1: distancePriceCalculation if available and is a valid number
    if (distancePriceCalculation !== null && 
        distancePriceCalculation !== undefined && 
        typeof distancePriceCalculation === 'number' && 
        !isNaN(distancePriceCalculation) && 
        distancePriceCalculation > 0) {
      return distancePriceCalculation;
    }
    
    // Priority 2: discountedPrice if available and is a valid number
    if (discountedPrice !== null && 
        discountedPrice !== undefined && 
        typeof discountedPrice === 'number' && 
        !isNaN(discountedPrice) && 
        discountedPrice > 0) {
      return discountedPrice;
    }
    
    // Default: no calculated price available
    return null;
  };

  // Notify parent component whenever calculated price changes
  useEffect(() => {
    if (onCalculatedPriceChange) {
      const calculatedPrice = getCalculatedPrice();
      onCalculatedPriceChange(calculatedPrice);
    }
  }, [distancePriceCalculation, discountedPrice, onCalculatedPriceChange]);

  const handleAgreedPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Mark that user has manually edited the price
    setHasUserEditedPrice(true);
    
    // Handle empty input
    if (inputValue === '' || inputValue === null || inputValue === undefined) {
      onTripDataChange({ ...tripData, agreedPrice: null });
      return;
    }
    
    // Convert to number and validate
    const numericValue = Number(inputValue);
    
    // Prevent negative values and invalid numbers
    if (isNaN(numericValue) || numericValue < 0) {
      return;
    }
    
    // Update trip data with the user-entered value
    onTripDataChange({ ...tripData, agreedPrice: numericValue });
  };


  
  // Add function to handle key press and prevent special characters
  const handleAgreedPriceKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow only numbers, backspace, delete, tab, escape, enter, and decimal point
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', '.'];
    const isNumber = /[0-9]/.test(e.key);
    
    if (!isNumber && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  // Add function to handle GST number key press
  const handleGstKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow only alphanumeric characters, backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    const isAlphaNumeric = /[a-zA-Z0-9]/.test(e.key);
    
    if (!isAlphaNumeric && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  // Add function to handle GST number change
  const handleGstChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Remove any non-alphanumeric characters and convert to uppercase
    const cleanedValue = inputValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    // Limit to 15 characters
    const limitedValue = cleanedValue.slice(0, 15);
    
    // Update trip data
    onTripDataChange({ ...tripData, vendorGst: limitedValue });
    
    // Clear GST related errors if any
    if (errors.vendorGst) {
      const newErrors = { ...errors };
      delete newErrors.vendorGst;
      onErrorsChange(newErrors);
    }
  };

  // Initialize Google Places API
  useEffect(() => {
    initGooglePlacesApi().catch((error: Error) => {
      console.error('Failed to initialize Google Places API:', error);
    });
  }, []);

  // Initialize destinations list from tripData
  useEffect(() => {
    // If we already have destinationCitiesList, use that
    if (tripData.destinationCitiesList?.length) {
      setDestinationsList(tripData.destinationCitiesList);
    }
    // Otherwise, if we have destinationCities and our list is empty, parse the string
    else if (tripData.destinationCities && destinationsList.length === 0) {
      // Split by pipe separator if it's a pipe-separated string, fallback to comma for backward compatibility
      const separator = tripData.destinationCities.includes(' | ') ? ' | ' : ',';
      const destinations = tripData.destinationCities.split(separator).map(d => d.trim()).filter(d => d);
      setDestinationsList(destinations);
    }
  }, [tripData.destinationCities, tripData.destinationCitiesList]);
  
  // Fetch place predictions when newDestination changes
  useEffect(() => {
    const fetchPredictions = async () => {
      if (!newDestination || newDestination.length < 2 || isMumbaiAirportService || isOnewayService || isPerPaxService || isPuneLocalService) {
        setPlacePredictions([]);
        return;
      }
      
      setIsLoadingPredictions(true);
      try {
        const predictions = await getPlacePredictions(newDestination);
        setPlacePredictions(predictions);
      } catch (error) {
        console.error('Error fetching place predictions:', error);
      } finally {
        setIsLoadingPredictions(false);
      }
    };
    
    const timer = setTimeout(fetchPredictions, 300); // Debounce API calls
    return () => clearTimeout(timer);
  }, [newDestination, isMumbaiAirportService, isOnewayService, isPuneLocalService, isPerPaxService]);
  
  // Update destinationCities string when destinationsList changes
  useEffect(() => {
    const destinationsString = destinationsList.join(' | ');
    if (destinationsString !== tripData.destinationCities) {
      onTripDataChange({
        ...tripData,
        destinationCities: destinationsString,
        destinationCitiesList: destinationsList.length > 0 ? destinationsList : undefined
      });
    }
  }, [destinationsList]);
  
  // Calculate distances between source city and destinations
  const calculateDistances = async () => {
    if (!tripData.sourceCity || destinationsList.length === 0) {
      setDestinationsWithDistance([]);
      setTotalDistance(null);
      return;
    }
    
    // Initialize destinations with distance calculation state
    const newDestinationsWithDistance = destinationsList.map(dest => ({
      name: dest,
      distance: null as number | null, // Explicitly type as number | null
      isCalculating: true
    }));
    
    setDestinationsWithDistance(newDestinationsWithDistance);
    
    // Calculate distance for each destination
    let sumDistance = 0;
    let lastPoint = tripData.sourceCity;
    
    for (let i = 0; i < destinationsList.length; i++) {
      try {
        const distance = await calculateDistance(lastPoint, destinationsList[i]);
        
        // Update the distance for this destination
        newDestinationsWithDistance[i] = {
          ...newDestinationsWithDistance[i],
          distance: distance, // Explicitly assign the number to distance property
          isCalculating: false
        };
        
        setDestinationsWithDistance([...newDestinationsWithDistance]);
        
        // Add to total and update last point for next calculation
        sumDistance += distance;
        lastPoint = destinationsList[i];
      } catch (error) {
        console.error(`Error calculating distance to ${destinationsList[i]}:`, error);
        
        // Mark as failed but don't block other calculations
        newDestinationsWithDistance[i] = {
          ...newDestinationsWithDistance[i],
          isCalculating: false
        };
        
        setDestinationsWithDistance([...newDestinationsWithDistance]);
      }
    }
    
    setTotalDistance(sumDistance);
  };
  
  // Calculate distances when source city or destinations change
  useEffect(() => {
    calculateDistances();
  }, [tripData.sourceCity, destinationsList]);
  
  // Function to add a new destination
  const handleAddDestination = () => {
    if (newDestination && !destinationsList.includes(newDestination)) {
      setDestinationsList([...destinationsList, newDestination]);
      setNewDestination('');
      
      // Clear any destination-related errors
      if (errors.destinationCities) {
        const newErrors = { ...errors };
        delete newErrors.destinationCities;
        onErrorsChange(newErrors);
      }
    }
  };
  
  // Function to remove a destination
  const handleRemoveDestination = (index: number) => {
    const newDestinations = [...destinationsList];
    newDestinations.splice(index, 1);
    setDestinationsList(newDestinations);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormContainer sx={{ mb: 0 }}>
        {/* Trip Information Header */}
        <HeaderBox>
    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
      Trip Information
    </Typography>
  </HeaderBox>
        {/* Form Content */}
        <ContentBox>
          <FormRow>
           {!isOtherService && <FormField sx={{ flex: '1 1 100%', minWidth: '240px' }}>
              <TextField
                fullWidth
                label="GST Number"
                name="vendorGst"
                value={tripData.vendorGst || ''}
                onChange={handleGstChange}
                onKeyDown={handleGstKeyPress}
                placeholder="Enter GST number (15 characters required)"
                inputProps={{ 
                  maxLength: 15,
                  style: { textTransform: 'uppercase' }
                }}
                error={!!(tripData.vendorGst && tripData.vendorGst.length > 0 && tripData.vendorGst.length !== 15)}
                helperText={
                  tripData.vendorGst && tripData.vendorGst.length > 0 
                    ? (tripData.vendorGst.length !== 15
                        ? `GST number must be exactly 15 characters (${tripData.vendorGst.length}/15)`
                        : `${tripData.vendorGst.length}/15 characters`)
                    : undefined
                }
              />
            </FormField>}
            <ServiceSelectionForm
              serviceOptions={serviceOptions}
              fleetOptions={fleetOptions}
              selectedServiceId={tripData.travelServiceDetailId?.toString() || ''}
              selectedFleetId={tripData.travelfleetDetailId?.toString() || ''}
              onServiceChange={handleServiceChange}
              onFleetChange={handleFleetChange}
              onAirportSelectionChange={handleAirportSelectionChange}
              onOnewaySelectionChange={handleOnewaySelectionChange}
              onPerPaxSelectionChange={handlePerPaxSelectionChange}
              errors={errors}
              isMumbaiAirportService={isMumbaiAirportService}
              isOnewayService={isOnewayService}
              isPerPaxService={isPerPaxService}
              isOtherService={isOtherService}
              airportPickupDrop={airportPickupDrop}
              onewayRoute={onewayRoute}
              perPaxRoute={perPaxRoute}
            />

            {/* Show outstation source city dropdown when outstation service is selected */}
           {!isOtherService && (isOutstationService ? (
              <FormField>
                <TextField
                  fullWidth
                  select
                  label={isOutstationService ? "" : "Source City"}
                  value={outstationSourceCity}
                  onChange={(e) => handleOutstationSourceCityChange(e.target.value)}
                  error={!!errors.sourceCity}
                  helperText={errors.sourceCity || "Select source city for outstation service"}
                  disabled={!isServiceSelected}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">Select Source City</option>
                  <option value="Pune">Pune</option>
                  <option value="Mumbai">Mumbai</option>
                </TextField>
              </FormField>
            ) : (
              /* Show normal source city field for non-outstation services */
              <FormField>
                <TextField
                  fullWidth
                  label="Source City"
                  name="sourceCity"
                  value={isPuneLocalService ? 'Pune' : tripData.sourceCity}
                  onChange={handleChange}
                  disabled={!isServiceSelected}
                  InputProps={{
                    readOnly: isMumbaiAirportService || isOnewayService || isPuneLocalService || isPerPaxService,
                  }}
                  error={!!errors.sourceCity}
                  helperText={errors.sourceCity}
                  placeholder={
                    !isServiceSelected ? "Please select a service type first" :
                    isMumbaiAirportService ? "Will be prefilled based on pickup/drop selection" :
                    isOnewayService ? "Will be prefilled based on route selection" :
                    isPuneLocalService ? "Automatically set to Pune for local service" :
                    isPerPaxService ? "Will be prefilled based on route selection" :
                    "Enter source city"
                  }
                  sx={{
                    ...(isMumbaiAirportService || isOnewayService || isPuneLocalService || isPerPaxService) && {
                      '& .MuiInputBase-input': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        cursor: 'not-allowed'
                      }
                    }
                  }}
                />
              </FormField>
            ))}

            {/* Hide destination cities field for Pune Local service */}
           {!isOtherService && (!isPuneLocalService && (
              <FormField>
                {/* For Mumbai Airport and Oneway services, show simple read-only text field */}
                {(isMumbaiAirportService || isOnewayService || isPerPaxService) ? (
                  <TextField
                    fullWidth
                    label="Destination City"
                    name="destinationCities"
                    value={tripData.destinationCities}
                    disabled={!isServiceSelected}
                    InputProps={{
                      readOnly: true,
                    }}
                    error={!!errors.destinationCities}
                    helperText={errors.destinationCities}
                    placeholder={
                      !isServiceSelected ? "Please select a service type first" :
                      isMumbaiAirportService
                        ? "Will be prefilled based on pickup/drop selection"
                        : "Will be prefilled based on route selection"
                    }
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        cursor: 'not-allowed'
                      }
                    }}
                  />
                ) : (
                  /* For Outstation and other services, show Google Places autocomplete with chips */
                  <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Autocomplete
                          freeSolo
                          options={placePredictions}
                          loading={isLoadingPredictions}
                          disabled={!isServiceSelected}
                          getOptionLabel={(option) => 
                            typeof option === 'string' ? option : option.description
                          }
                          filterOptions={(x) => x} // Disable built-in filtering
                          inputValue={newDestination}
                          onInputChange={(event, value) => {
                            setNewDestination(value || '');
                          }}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              const selectedPlace = typeof newValue === 'string' 
                                ? newValue 
                                : newValue.description;
                              
                              // Just update the input value, don't add to destinations list yet
                              setNewDestination(selectedPlace);
                              resetSessionToken(); // Reset session token after selection
                            }
                          }}
                          renderOption={(props, option) => (
                            <li {...props} key={option.place_id}>
                              <Box>
                                <Typography variant="body1" component="span">
                                  {option.structured_formatting?.main_text || option.description}
                                </Typography>
                                {option.structured_formatting?.secondary_text && (
                                  <Typography 
                                    variant="body2" 
                                    component="span" 
                                    sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}
                                  >
                                    {option.structured_formatting.secondary_text}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              label={
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                  {isOutstationService ? "Destination Cities" : "Destination City"}
                                  {errors.destinationCities && (
                                    <Typography 
                                      component="span" 
                                      sx={{ 
                                        color: '#f44336', 
                                        ml: 1, 
                                        fontSize: '0.75rem' 
                                      }}
                                    >
                                      {errors.destinationCities}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              error={!!errors.destinationCities}
                              placeholder={!isServiceSelected ? "Please select a service type first" : "Search for a city"}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingPredictions ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                              sx={DestinationAutocomplete}
                            />
                          )}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!newDestination || !isServiceSelected}
                        onClick={handleAddDestination}
                        sx={AddButton}
                      >
                        <AddIcon fontSize="small" />
                      </Button>
                    </Box>
                    
                    {/* Show chips below the input */}
                    {destinationsList.length > 0 && (
                      <Box sx={DestinationChipsContainer}>
                        {destinationsWithDistance.map((destination, index) => (
                          <div
                            key={index}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            style={{
                              display: 'inline-block',
                              margin: '0 4px 4px 0',
                              cursor: 'grab'
                            }}
                          >
                            <Chip
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={ChipNumberCircle}>
                                    {index + 1}
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                    {destination.name}
                                    {destination.isCalculating && (
                                      <CircularProgress 
                                        size={8} 
                                        sx={{ ml: 0.5, verticalAlign: 'middle' }} 
                                      />
                                    )}
                                    {!destination.isCalculating && destination.distance !== null && (
                                      <Typography 
                                        component="span" 
                                        sx={{ 
                                          ml: 0.5, 
                                          fontSize: '0.7rem', 
                                          color: 'rgba(255, 255, 255, 0.7)',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        ({destination.distance} km)
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                              }
                              onDelete={() => handleRemoveDestination(index)}
                              color="primary"
                              variant="outlined"
                              size="small"
                              sx={DestinationChipStyles}
                            />
                          </div>
                        ))}
                      </Box>
                    )}

                    {/* Total distance display */}
                    {totalDistance !== null && destinationsWithDistance.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            fontSize: '0.75rem',
                            fontStyle: 'italic'
                          }}
                        >
                          Total route distance: <strong>{totalDistance} km</strong>
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {/* Hidden field */}
                <input 
                  type="hidden" 
                  name="destinationCities" 
                  value={tripData.destinationCities} 
                />
              </FormField>
            ))}

           {!isOtherService &&  <FormField>
              <DateTimePicker
                label="Start Date & Time"
                value={tripData.startDatetime}
                onChange={(date) => handleDateChange('startDatetime', date)}
                format="dd/MM/yyyy hh:mm a"
                ampm={true}
                disabled={!isServiceSelected}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.startDatetime,
                    helperText: errors.startDatetime,
                    placeholder: !isServiceSelected ? "Please select a service type first" : undefined
                  }
                }}
              />
            </FormField>}

            {/* Only show end date for services that need it */}
            {!shouldHideEndDate && (
              <FormField>
                <DateTimePicker
                  label="End Date & Time"
                  value={tripData.endDatetime}
                  onChange={(date) => handleDateChange('endDatetime', date)}
                  format="dd/MM/yyyy hh:mm a"
                  ampm={true}
                  disabled={!isServiceSelected}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.endDatetime,
                      helperText: errors.endDatetime,
                      placeholder: !isServiceSelected ? "Please select a service type first" : undefined
                    }
                  }}
                />
              </FormField>
            )}

            <FormField>
              <TextField
                fullWidth
                // label="Lead Source"
                name="request"
                value={tripData.request}
                onChange={handleChange}
                disabled={!isServiceSelected}
                error={!!errors.request}
                helperText={errors.request}
                placeholder={!isServiceSelected ? "Please select a service type first" : "Select lead source"}
                select
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Lead Source</option>
                <option value="Google">Google</option>
                <option value="Justdial">Justdial</option>
                <option value="Reference">Reference</option>
                <option value="Existing Customer">Existing Customer</option>
                <option value="Others">Others</option>
              </TextField>
            </FormField>

            <FormField>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                value={tripData.notes}
                onChange={handleChange}
                disabled={!isServiceSelected}
                multiline
                rows={4}
                placeholder={!isServiceSelected ? "Please select a service type first" : "Enter any additional notes"}
              />
            </FormField>
          </FormRow>
        </ContentBox>
        
        {/* Pricing Calculator Section */}
        {showPricingCalculator && selectedFleetInfo && (
          <TripPricingCalculator
            selectedFleetInfo={selectedFleetInfo}
            serviceType={getServiceType()}
            calculatedDays={calculatedDays}
            customKm={customKm}
            totalDistance={totalDistance}
            onDiscountedPriceChange={handleDiscountedPriceChange}
            onDistancePriceCalculationChange={handleDistancePriceCalculationChange}
            onEstimatedPriceChange={handleEstimatedPriceChange}
          />
        )}

        {/* Agreed Price Input Section */}
        {showPricingCalculator && (
          <Box sx={PricingSection}>
            <Typography variant="body2" sx={{ 
              color: '#2196f3', 
              fontSize: '0.9rem', 
              fontWeight: 'bold', 
              mb: 1 
            }}>
              Agreed Price with Customer:
            </Typography>
            <TextField
              size="small"
              type="number"
              placeholder={(() => {
                const calculatedPrice = getCalculatedPrice();
                return calculatedPrice !== null ? `Calculated Price :  ${calculatedPrice}` : "Enter agreed price";
              })()}
              value={(() => {
                // If user has manually entered a price, always use that
                if (hasUserEditedPrice && (tripData?.agreedPrice !== null && tripData?.agreedPrice !== undefined)) {
                  return tripData.agreedPrice;
                }
                
                // If user hasn't edited and we have a user-entered price, use that
                if (tripData?.agreedPrice !== null && tripData?.agreedPrice !== undefined) {
                  return tripData.agreedPrice;
                }
                
                // If no user price and user hasn't edited, show calculated price for pre-fill
                if (!hasUserEditedPrice) {
                  const calculatedPrice = getCalculatedPrice();
                  if (calculatedPrice !== null) {
                    return calculatedPrice;
                  }
                }
                
                // Default to empty
                return '';
              })()}
              onChange={handleAgreedPriceChange}
              onKeyDown={handleAgreedPriceKeyPress}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              onFocus={() => {
                // When user focuses on the field, if it shows calculated price, 
                // mark as user edited so they can modify it
                const calculatedPrice = getCalculatedPrice();
                if (!hasUserEditedPrice && calculatedPrice !== null && 
                    (tripData?.agreedPrice === null || tripData?.agreedPrice === undefined)) {
                  // Pre-fill the tripData with calculated price so user can edit it
                  onTripDataChange({ ...tripData, agreedPrice: calculatedPrice });
                  setHasUserEditedPrice(true);
                }
              }}
              InputProps={{
                startAdornment: <span style={{ color: '#2196f3', marginRight: '4px', fontWeight: 'bold' }}>₹</span>,
                inputProps: { min: 0 }
              }}
              sx={AgreedPriceInput}
            />
          </Box>
        )}

        {/* Submit buttons */}
        <FooterBox
          display="flex"
          justifyContent="flex-end"
          mt={0}
          pt={2}
          px={3}
          pb={2}
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: '#1e1e1e'
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            sx={{ mr: 2 }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Trip...' : 'Create Trip'}
          </Button>
        </FooterBox>
      </FormContainer>
    </Box>
  );
};

export default TripInformationSection;